import TabsList from "molecules/TabsList";
import React, { useEffect, useState } from "react";
import MyTimeSheet from "./MyTimeSheet";
import PendingTimeSheet from "./PendingTimeSheet";
import DeclinedTimeSheet from "./DeclinedTimeSheet";
import styled from "styled-components";
import { useSelector } from "react-redux";
import {
  getMenuList,
  getUserInfo,
  getUserList,
  getUserRoleId,
} from "redux/selectors";
import TimeSheetHistory from "./History";
import {
  Breadcrumb,
  ConfigProvider,
  DatePicker,
  Form,
  Input,
  Modal,
  notification,
  Result,
  Spin,
} from "antd";
import en_GB from "antd/lib/locale-provider/en_GB";
import "moment/locale/en-gb";
import moment from "moment";
import Button from "atoms/Button";
import { generateNiceWeekFromWeek, generateTimesheetWeek } from "./useWeekData";
import queryKeys from "common/queryKey";
import { useFetch } from "hooks/useFetch";
import { useLocation } from "react-router-dom";

moment.locale("en-gb");

export const ONGOING = "ongoing"; // haven't submitted even once
export const WITHDRAWN = "withdrawn"; // submitted but withdrawn
export const DECLINED = "declined"; // submitted but declined by the manager
export const APPROVED = "approved"; // submitted and approved by the manager
export const PENDING = "pending"; // submitted but not yet approved or declined
export const DRAFT = "draft"; // saved but not submitted
export const TOTAL_WORKING_HOURS = 8;
export const MAX_WORKING_HOURS = 12;
export const MIN_WORKING_HOURS = 8;

const NO_PERMISSION_MESSAGE = "You don't have permission to perform this action.";

// ---------------------------------------------------------------------
// PERMISSION FIX: every timesheet tab has its own menu entry carrying
// { view, create, edit, delete }:
//   My Timesheet      -> "mytimesheet"
//   Pending Approvals -> "pendingapprovalstimesheet"
//   Declined          -> "declinedtimesheet"
// ---------------------------------------------------------------------
const resolvePermission = (menuItems, menuKey) => {
  const menu = (menuItems || []).find((item) => item?.key === menuKey);
  if (!menu || menu.access !== true) {
    return {
      access: false,
      view: false,
      create: false,
      edit: false,
      delete: false,
    };
  }
  return {
    access: true,
    view: menu.permission?.view !== false,
    create: menu.permission?.create === true,
    edit: menu.permission?.edit === true,
    delete: menu.permission?.delete === true,
  };
};

const TimeSheet = () => {
  const [tabId, setTabId] = useState("mytimesheet");
  const [showTimesheet, setShowTimesheet] = useState(false);
  const [selectedTimesheetPeriod, setSelectedTimesheetPeriod] = useState(null);
  const [showAddTimeSheet, setShowAddTimeSheet] = useState(false);
  const [duplicateTimesheet, setDuplicateTimesheet] = useState(false);
  const [navigatedTab, setNavigatedTab] = useState(null);
  const location = useLocation();

  const [selectedWeek, setSelectedWeek] = useState(null);
  const [duplicateWeek, setDuplicateWeek] = useState(null);

  const [form] = Form.useForm();

  const userRoleId = useSelector(getUserRoleId);
  const menuList = useSelector(getMenuList);

  let Menulist = menuList[userRoleId] || Object.values(menuList)[0] || menuList;

  Menulist = Array.isArray(Menulist) ? Menulist : [];

  // PERMISSION FIX: one permission object per tab
  const myTimesheetPermission = resolvePermission(Menulist, "mytimesheet");
  const pendingPermission = resolvePermission(
    Menulist,
    "pendingapprovalstimesheet"
  );
  const declinedPermission = resolvePermission(Menulist, "declinedtimesheet");

  const haveMyTimeSheetAccess =
    myTimesheetPermission.access && myTimesheetPermission.view;
  const havePendingApprovalAccess =
    pendingPermission.access && pendingPermission.view;
  const haveDeclinedAccess =
    declinedPermission.access && declinedPermission.view;

  // Creating a timesheet (Add Timesheet button / modal / duplicate) needs
  // "create" on the My Timesheet entry.
  const canCreateTimesheet = myTimesheetPermission.create;

  const cancelAddTimeSheet = () => {
    setShowAddTimeSheet(false);
  };

  const handleClickTimeSheet = (period, duplicateSheet = false) => {
    setSelectedTimesheetPeriod(period);
    duplicateSheet && setDuplicateTimesheet(duplicateSheet);
    setShowTimesheet(true);
    setShowAddTimeSheet(false);
    form.resetFields();
  };

  // PERMISSION FIX: used only by the Add Timesheet modal (create flow)
  const handleAddTimesheet = (period, duplicateSheet = false) => {
    if (!canCreateTimesheet) {
      notification.warning({ message: NO_PERMISSION_MESSAGE });
      return;
    }
    handleClickTimeSheet(period, duplicateSheet);
  };

  const handleTabSelect = (id) => {
    setTabId(id);
  };

  useEffect(() => {
    // Coming from card click — has both activeTab and period
    if (location.state?.activeTab && location.state?.period) {
      setSelectedTimesheetPeriod(location.state.period);
      setNavigatedTab(location.state.activeTab);
      setTabId(location.state.activeTab);
      setShowTimesheet(true);
      return;
    }

    // Coming from Add Timesheet flow — has period only.
    // PERMISSION FIX: this is a create flow, so it needs create access.
    if (location.state?.period) {
      if (!canCreateTimesheet) return;
      setSelectedTimesheetPeriod(location.state.period);
      setDuplicateTimesheet(location.state.duplicateSheet || false);
      setShowTimesheet(true);
      return;
    }

    // Coming from card click with no period (fallback)
    if (location.state?.activeTab) {
      setNavigatedTab(location.state.activeTab);
      setTabId(location.state.activeTab);
      setShowTimesheet(true);
    }
  }, [
    location.state?.activeTab,
    location.state?.period,
    location.state?.navigatedAt,
    canCreateTimesheet,
  ]);

  useEffect(() => {
    // Check BOTH navigatedTab state AND location.state directly
    // because navigatedTab may not have updated yet when this effect runs
    if (navigatedTab || location.state?.activeTab) return;

    if (haveMyTimeSheetAccess) {
      setTabId("mytimesheet");
    } else if (havePendingApprovalAccess) {
      setTabId("pendingapprovalstimesheet");
    } else if (haveDeclinedAccess) {
      setTabId("declinedtimesheet");
    }
  }, [
    haveMyTimeSheetAccess,
    havePendingApprovalAccess,
    haveDeclinedAccess,
    navigatedTab,
    location.state?.activeTab,
  ]);

  const items = [
    haveMyTimeSheetAccess && {
      id: "mytimesheet",
      label: "My Timesheet",
      component: (
        <MyTimeSheet
          selectedTabId={tabId}
          selectedPeriod={selectedTimesheetPeriod}
          duplicateTimesheet={duplicateTimesheet}
          permission={myTimesheetPermission}
        />
      ),
    },
    havePendingApprovalAccess && {
      id: "pendingapprovalstimesheet",
      label: "Pending Approvals",
      component: (
        <PendingTimeSheet
          selectedPeriod={selectedTimesheetPeriod}
          selectedTabId={tabId}
          permission={pendingPermission}
        />
      ),
    },
    haveDeclinedAccess && {
      id: "declinedtimesheet",
      label: "Declined",
      component: (
        <DeclinedTimeSheet
          selectedPeriod={selectedTimesheetPeriod}
          permission={declinedPermission}
        />
      ),
    },
  ];

  // PERMISSION FIX: no timesheet tab allowed at all -> block the page
  // (all hooks are above, so returning early is safe)
  if (
    Menulist.length > 0 &&
    !haveMyTimeSheetAccess &&
    !havePendingApprovalAccess &&
    !haveDeclinedAccess
  ) {
    return (
      <Result
        status="403"
        title="403"
        subTitle="Sorry, you don't have permission to access this page."
        extra={
          <Button type="primary" onClick={() => (window.location.href = "/")}>
            Go to Home
          </Button>
        }
      />
    );
  }

  return (
    <TimeSheetWrapper>
      {showTimesheet ? (
        <div>
          <Breadcrumb separator=">">
            <Breadcrumb.Item
              onClick={() => {
                setShowTimesheet(false);
                setSelectedTimesheetPeriod(null);
              }}
            >
              Back
            </Breadcrumb.Item>
            {/* <Breadcrumb.Item>{selectedTimesheet?.period}</Breadcrumb.Item> */}
            <Breadcrumb.Item>Timesheet</Breadcrumb.Item>
          </Breadcrumb>

          <TabsList
            items={items}
            handleTabSelect={handleTabSelect}
            tabId={tabId}
          />
        </div>
      ) : (
        <div>
          <div className="heading">
            <span>Timesheet</span>
            {/* PERMISSION FIX: only users with create access see "Add Timesheet" */}
            {canCreateTimesheet && (
              <span>
                <Button
                  onClick={() => {
                    setShowAddTimeSheet(true);
                    setSelectedWeek(null);
                    setDuplicateWeek(null);
                    form.resetFields();
                  }}
                  type="primary"
                >
                  {" "}
                  Add Timesheet{" "}
                </Button>
              </span>
            )}
          </div>

          <TimeSheetHistory
            handleClickTimeSheet={handleClickTimeSheet}
            permission={myTimesheetPermission}
          />
        </div>
      )}

      {canCreateTimesheet && (
        <StyledModal
          open={showAddTimeSheet}
          onCancel={cancelAddTimeSheet}
          title={"Add Timesheet"}
          width={500}
          footer={null}
          closable={true}
          maskClosable={false}
          centered={true}
          destroyOnClose={true}
        >
          <AddTimesheet
            form={form}
            handleClickTimeSheet={handleAddTimesheet}
            selectedWeek={selectedWeek}
            setSelectedWeek={setSelectedWeek}
            duplicateWeek={duplicateWeek}
            setDuplicateWeek={setDuplicateWeek}
          />
        </StyledModal>
      )}
    </TimeSheetWrapper>
  );
};

export default TimeSheet;

const AddTimesheet = ({
  form,
  handleClickTimeSheet,
  selectedWeek,
  setSelectedWeek,
  duplicateWeek,
  setDuplicateWeek,
}) => {
  const userInfo = useSelector(getUserInfo);

  const userList = useSelector(getUserList);

  const currentUser = userList.find((user) => user.userid === userInfo.userid);

  const country = currentUser?.managingoffice?.[0];

  const weekStart = (week) => (week ? week.clone().startOf("isoWeek") : null);

  var weekEnd = (week) => (week ? week.clone().endOf("isoWeek") : null);

  const weekPeriod = selectedWeek
    ? generateTimesheetWeek(weekStart(selectedWeek), weekEnd(selectedWeek))
    : null;

  const period = selectedWeek ? generateNiceWeekFromWeek(weekPeriod) : "-";

  const { getTimesheet } = queryKeys;

  const { data: timesheetDataRes, isLoading: selectedTimesheetLoading } =
    useFetch(
      `${getTimesheet.key}-week:${weekPeriod}`,
      `${getTimesheet.url}?week=${weekPeriod}&employeeId=${userInfo.employer_id}`,
      {
        enabled: !!weekPeriod,
      }
    );

  const duplicateWeekPeriod = duplicateWeek
    ? generateTimesheetWeek(weekStart(duplicateWeek), weekEnd(duplicateWeek))
    : false;

  const {
    data: duplicateTimesheetDataRes,
    isLoading: isDuplicateTimesheetLoading,
  } = useFetch(
    `${getTimesheet.key}-week:${duplicateWeekPeriod}`,
    `${getTimesheet.url}?week=${duplicateWeekPeriod}&employeeId=${userInfo.employer_id}`,
    {
      enabled: !!duplicateWeekPeriod,
    }
  );

  const hasTimesheet = timesheetDataRes?.response?.response?.status || false;

  useEffect(() => {
    if (hasTimesheet) {
      setSelectedWeek(null);
      form.resetFields();
      notification.error({
        message: `Timesheet for week ${period} already exists`,
      });
    }
  }, [hasTimesheet, period, form]);

  const handleSubmit = () => {
    if (selectedWeek !== null && period !== "-") {
      const duplicateTimesheet =
        duplicateTimesheetDataRes?.response?.response?.timeSheet;

      if (duplicateTimesheet) {
        handleClickTimeSheet(
          period,
          duplicateTimesheet?.map((item) => {
            item.status = "pending";
            return item;
          })
        );
      } else {
        handleClickTimeSheet(period);
      }

      setSelectedWeek(null);
      setDuplicateWeek(null);
    }
  };

  return (
    <div className="add-timesheet-box">
      <Form
        form={form}
        layout="vertical"
        className="mt-2"
        onFinish={handleSubmit}
      >
        <Spin spinning={selectedTimesheetLoading}>
          <ConfigProvider locale={en_GB}>
            <Form.Item
              label="Select Week"
              name="week"
              rules={[{ required: true, message: "Please select week" }]}
            >
              <DatePicker
                picker="week"
                className="w-100"
                value={selectedWeek}
                onChange={(date) => {
                  setSelectedWeek(date);
                }}
              />
            </Form.Item>
          </ConfigProvider>
        </Spin>

        <Form.Item label="Period">
          <Input value={period} placeholder="Select Period" disabled={true} />
        </Form.Item>

        <ConfigProvider locale={en_GB}>
          <Form.Item label="Duplicate Week" name="duplicateWeek">
            <DatePicker
              picker="week"
              className="w-100"
              value={duplicateWeek}
              onChange={(date) => {
                setDuplicateWeek(date);
              }}
            />
          </Form.Item>
        </ConfigProvider>

        <Button
          htmlType="submit"
          type="primary"
          disabled={selectedTimesheetLoading || isDuplicateTimesheetLoading}
        >
          Add
        </Button>
      </Form>
    </div>
  );
};

export const TimeSheetStatus = ({ status }) => {
  return (
    <TimeSheetStatusWrap>
      {status === "pending" && <span className="status pending">Pending</span>}
      {status === "approved" && (
        <span className="status approved">Approved</span>
      )}
      {status === "declined" && (
        <span className="status declined">Declined</span>
      )}
      {status === "withdrawn" && (
        <span className="status withdrawn">Withdrawn</span>
      )}
    </TimeSheetStatusWrap>
  );
};

const TimeSheetStatusWrap = styled.div`
  .status {
    font-size: 14px;
    font-weight: 500;
  }

  .pending {
    color: #ffa702;
  }
  .approved {
    color: #32b900;
  }
  .declined {
    color: #ff7676;
  }
  .withdrawn {
    color: #ff7676;
  }
`;

const TimeSheetWrapper = styled.div`
  .timesheet-header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .left-icon {
      width: 46px;
      height: 46px;
      &:hover {
        cursor: pointer;
        opacity: 0.7;
      }
    }
    .right-icon {
      margin-left: -10px;
      width: 46px;
      height: 46px;
      &:hover {
        cursor: pointer;
        opacity: 0.7;
      }
    }
  }
  .date {
    font-weight: 600;
  }
  .copy-icon {
    cursor: pointer;
    margin-left: -4px;
  }
  .submit-button,
  .history-button,
  .add-more,
  .close-button {
    border-radius: 5px;
    margin-bottom: 4px;
    margin-left: 0px;
  }
  .duplicate-week {
    font-size: 14px;
    font-weight: 500;
  }
  .no-timesheet {
    text-align: center;
    margin-top: 1rem;
    color: #bfbfbf;
    font-weight: 600;
    font-size: 1.2rem;
  }
  .current-week {
    background: #f2f6ff;
    padding: 4px 8px;
    border-radius: 5px;
    cursor: pointer;
    &:hover {
      background: #e6f0ff;
    }
  }
  .ant-breadcrumb-link {
    cursor: pointer;
  }
  .heading {
    font-size: 18px;
    font-weight: 500;
    margin-bottom: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  button.ant-btn {
    border-radius: 5px;
  }
`;

const StyledModal = styled(Modal)`
  .ant-modal-close-x {
    .anticon-close.ant-modal-close-icon,
    svg {
      width: 20px;
      height: 20px;
    }
  }
  .ant-modal-header {
    border-bottom: none;
    padding-top: 10px;
  }
  .ant-modal-title {
    font-weight: 500;
    text-align: left;
    color: #1b1b1b;
  }
  .ant-modal-content {
    border-radius: 10px;
  }
  .anticon {
    color: #000000;
    width: 14px;
    height: 14px;
  }
  .ant-modal-body {
    padding-top: 0px;
  }
  .ant-modal-footer {
    display: none;
  }
  .ant-btn-text:focus,
  .ant-btn-text:hover {
    background: #26a4ff;
    color: #ffffff;
  }
  .ant-checkbox.ant-checkbox-checked {
    .ant-checkbox-inner {
      background-color: #02b209 !important;
      border-color: #02b209 !important;
    }
  }
  .ant-upload-span {
    cursor: pointer;
  }
  .add-timesheet-box {
    padding: 20px;
    border: 1px solid #e8e8e8;
  }
`;